import { createRef, FC, ReactNode, useCallback, useEffect } from 'react'

import type { Placement } from '@floating-ui/react-dom'
import { Menu } from '@headlessui/react'
import DotsHorizontalIcon from '@heroicons/react/outline/esm/DotsHorizontalIcon.js'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { useBackgroundContext } from '../BackgroundContext/index.js'
import { Divider } from '../Divider/Divider.js'
import { Hotkey } from '../Hotkey/Hotkey.js'
import { Icon } from '../Icon/index.js'
import type { IconComponentType } from '../Icon/index.js'
import { ChevronDownIcon } from '../icons/index.js'
import { HTMLTribeProps } from '../types/index.js'
import {
  FloatingContextProvider,
  FloatingWrapper,
  useFloatingContext,
} from '../utils/floating/index.js'
import { runIfFn } from '../utils/index.js'
import { DropdownProvider, useDropdown } from './DropdownContext.js'

interface DropdownRenderProps {
  open: boolean
}

export type DropdownProps = Omit<HTMLTribeProps<'div'>, 'children'> & {
  /**
   * Alias for popper.js placement, see https://floating-ui.com/docs/computePosition#placement
   */
  placement?: Placement
  children?: ReactNode | ((props: DropdownRenderProps) => ReactNode)
}

/**
 * Menus offer an easy way to build custom, accessible dropdown components with robust support for keyboard navigation.
 */
export const Dropdown = ({
  children,
  as,
  className,
  placement = 'bottom-start',
}: DropdownProps) => {
  return (
    <Menu
      as={as || 'div'}
      className={twMerge(clsx('relative inline-block text-left', className))}
    >
      {renderProps => (
        <FloatingContextProvider placement={placement}>
          <DropdownProvider open={renderProps.open}>
            {runIfFn(children, renderProps)}
          </DropdownProvider>
        </FloatingContextProvider>
      )}
    </Menu>
  )
}

export type ButtonProps = HTMLTribeProps<'button'> & {
  icon?: IconComponentType
  leadingIcon?: IconComponentType
}

const Button: FC<ButtonProps> = props => {
  const { children, icon, leadingIcon, className, disabled, ...rest } = props
  const { reference } = useFloatingContext()

  const { backgroundType } = useBackgroundContext()
  const buttonClsx = [
    backgroundType === 'main' &&
      'text-content-subdued bg-surface hover:bg-surface-hovered',
    backgroundType === 'surface' &&
      'text-content-subdued bg-surface hover:bg-surface-hovered',
    backgroundType === 'secondary' &&
      'text-content-on-topbar bg-topbar hover:bg-topbar',
  ]

  return (
    <Menu.Button
      ref={reference}
      className={twMerge(
        clsx(
          'inline-flex justify-center items-center w-full rounded-md border border-line shadow-sm px-4 py-2 text-sm font-medium ',
          buttonClsx,
          disabled && 'opacity-60 cursor-default pointer-events-none',
          'focus:outline-none focus-visible:ring',
          className,
        ),
      )}
      disabled={disabled}
      {...rest}
    >
      <span className="sr-only">Open options</span>
      {leadingIcon && <Icon className="-ms-1 me-2 h-5 w-5">{leadingIcon}</Icon>}
      {children}
      {!leadingIcon && (
        <Icon className="-me-1 ms-2 h-5 w-5">
          {icon || <ChevronDownIcon />}
        </Icon>
      )}
    </Menu.Button>
  )
}

const ButtonMinimal: FC<ButtonProps & { as?: 'button' | 'div' }> = props => {
  const { className, children, disabled, ...rest } = props
  const { reference } = useFloatingContext()

  const { backgroundType } = useBackgroundContext()
  const buttonClsx = [
    backgroundType === 'main' &&
      'text-content-on-background-subdued bg-background hover:text-content-on-background-hovered',
    backgroundType === 'surface' &&
      'text-content-subdued bg-surface hover:text-content-hovered',
    backgroundType === 'secondary' &&
      'text-content-on-topbar bg-topbar hover:text-content-on-topbar',
  ]

  return (
    <Menu.Button
      ref={reference}
      className={twMerge(
        clsx(
          'rounded-full flex items-center',
          buttonClsx,
          disabled && 'opacity-60 cursor-default pointer-events-none',
          'focus:outline-none focus-visible:ring',
          className,
        ),
      )}
      disabled={disabled}
      {...rest}
    >
      {children || (
        <>
          <span className="sr-only">Open options</span>
          <DotsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
        </>
      )}
    </Menu.Button>
  )
}

export type DropdownItemsProps = HTMLTribeProps<'div'> & {
  width?: 'md' | 'full'
  noWrapper?: boolean
}
const Items: FC<DropdownItemsProps> = props => {
  const { children, className, width = 'md', noWrapper, ...rest } = props
  const { open } = useDropdown()
  const { triggerRef } = useFloatingContext()
  const contentNode = createRef<HTMLDivElement>()

  const close = useCallback(() => {
    triggerRef.current?.click()
  }, [triggerRef])

  const focusContent = () => {
    if (contentNode == null) {
      return
    }

    requestAnimationFrame(() => {
      if (contentNode.current == null) {
        return
      }
      contentNode.current.focus()
    })
  }

  const FocusTrigger = () => {
    useEffect(() => {
      focusContent()
    }, [])
    return null
  }

  if (noWrapper && open) {
    return (
      <Menu.Items
        ref={contentNode}
        static
        className={clsx(
          width === 'md' && 'min-w-[224px] sm:max-w-xs',
          'p-1 rounded-md shadow-lg bg-surface border border-line',
          'focus-visible:ring focus:outline-none',
          className,
        )}
        {...rest}
      >
        {open && <FocusTrigger />}
        {runIfFn(children, { close })}
      </Menu.Items>
    )
  }

  return (
    <FloatingWrapper open={open} skipTransition>
      <Menu.Items
        ref={contentNode}
        static
        className={clsx(
          width === 'md' && 'min-w-[224px] sm:max-w-xs',
          'p-1 rounded-md shadow-lg bg-surface border border-line',
          'focus-visible:ring focus:outline-none',
          className,
        )}
        {...rest}
      >
        {open && <FocusTrigger />}
        {runIfFn(children, { close })}
      </Menu.Items>
    </FloatingWrapper>
  )
}

export type DropdownItemProps = HTMLTribeProps<'button'> & {
  disabled?: boolean
  leadingIcon?: IconComponentType
  hotkey?: string
}

const Item: FC<DropdownItemProps> = props => {
  const { as, children, leadingIcon, disabled, className, hotkey, ...rest } =
    props

  const Component = as || 'button'
  const type = Component === 'button' ? 'button' : undefined

  return (
    <Menu.Item disabled={disabled}>
      {({ active }) => (
        <Component
          type={type}
          className={clsx(
            active
              ? 'bg-surface-hovered text-content-hovered'
              : 'bg-surface text-content-subdued',
            'group flex items-center px-2 py-2 w-full rounded-md',
            disabled
              ? 'opacity-60 cursor-default pointer-events-none'
              : 'cursor-pointer',
            className,
          )}
          {...rest}
        >
          {leadingIcon && (
            <Icon className="me-3 h-5 w-5 text-content-subdued group-hover:text-content-hovered shrink-0">
              {leadingIcon}
            </Icon>
          )}
          <span className="grow text-left min-w-0 truncate break-words">
            {children}
          </span>
          {hotkey && <Hotkey className="ms-3 shrink-0">{hotkey}</Hotkey>}
        </Component>
      )}
    </Menu.Item>
  )
}

const DropdownDivider = ({ className }: { className?: string }) => {
  return <Divider padding="xs" className={clsx('-mx-1', className)} />
}

Dropdown.Button = Button
Dropdown.ButtonMinimal = ButtonMinimal
Dropdown.Items = Items
Dropdown.Item = Item
Dropdown.Divider = DropdownDivider
